// Tasks view (audit #15) — general-purpose reminder system.
import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Plus, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export default function TasksView({ currentUser, showToast = () => {} }) {
  const [tasks, setTasks]       = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [filter, setFilter]     = React.useState('open');
  const [adding, setAdding]     = React.useState(false);
  const [t, setT]               = React.useState({ title: '', notes: '', due_at: '', priority: 'normal' });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const j = await apiFetch(`/api/tasks?status=${filter}`);
      setTasks(j.tasks || []);
    } catch (err) {
      showToast(`Load failed: ${err.message}`);
    } finally { setLoading(false); }
  }, [filter, showToast]);
  React.useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!t.title.trim()) { showToast('Title required.'); return; }
    try {
      await apiFetch('/api/tasks', { method: 'POST', body: JSON.stringify({ ...t, due_at: t.due_at || null }) });
      setT({ title: '', notes: '', due_at: '', priority: 'normal' });
      setAdding(false);
      load();
    } catch (err) { showToast(`Add failed: ${err.message}`); }
  };

  const setStatus = async (id, status) => {
    try {
      await apiFetch('/api/tasks', { method: 'PATCH', body: JSON.stringify({ id, status }) });
      load();
    } catch (err) { showToast(`Update failed: ${err.message}`); }
  };

  const remove = async (id) => {
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: 'Delete this task?',
      confirmLabel: 'Delete task', danger: true,
    }) : Promise.resolve(window.confirm('Delete this task?')));
    if (!ok) return;
    try {
      await apiFetch(`/api/tasks?id=${id}&hard=1`, { method: 'DELETE' });
      load();
    } catch (err) { showToast(`Delete failed: ${err.message}`); }
  };

  const isOverdue = (task) => task.due_at && task.status === 'open' && new Date(task.due_at) < new Date();

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white rounded-xl p-5">
        <div className="text-amber-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Tasks</div>
        <h1 className="text-xl font-bold mt-1">Your reminder list</h1>
        <p className="text-slate-300 text-sm mt-1">Anything not tied to an automated drip — callbacks, follow-up docs, renewal nudges.</p>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {['open','done','all'].map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 ${filter === s ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-300 text-slate-700 hover:border-slate-500'}`}>{s}</button>
          ))}
        </div>
        <button onClick={() => setAdding(!adding)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-1.5"><Plus size={14}/> New task</button>
      </div>

      {adding && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
          <input value={t.title} onChange={e => setT({ ...t, title: e.target.value })} placeholder="What needs to happen?" autoFocus className="w-full text-sm border border-slate-300 rounded-md px-3 py-2"/>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input type="datetime-local" value={t.due_at} onChange={e => setT({ ...t, due_at: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            <select value={t.priority} onChange={e => setT({ ...t, priority: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <textarea value={t.notes} onChange={e => setT({ ...t, notes: e.target.value })} placeholder="Notes (optional)" rows={2} className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
          <div className="flex gap-2">
            <button onClick={add} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-1.5 rounded-md">Save</button>
            <button onClick={() => setAdding(false)} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 p-4">Loading…</div>
      ) : tasks.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500 text-sm">No tasks. {filter === 'open' && 'You\'re all caught up.'}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
          {tasks.map(task => {
            const over = isOverdue(task);
            const ptone = task.priority === 'urgent' ? 'bg-rose-100 text-rose-800 border-rose-300' : task.priority === 'high' ? 'bg-amber-100 text-amber-800 border-amber-300' : task.priority === 'low' ? 'bg-slate-100 text-slate-600 border-slate-200' : 'bg-sky-100 text-sky-800 border-sky-200';
            return (
              <div key={task.id} className={`px-4 py-3 flex items-center gap-3 ${task.status === 'done' ? 'opacity-50' : ''}`}>
                <button onClick={() => setStatus(task.id, task.status === 'done' ? 'open' : 'done')} className={`shrink-0 w-6 h-6 rounded-full border-2 ${task.status === 'done' ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-emerald-500'} flex items-center justify-center`}>
                  {task.status === 'done' && <CheckCircle size={14}/>}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    <span className={task.status === 'done' ? 'line-through' : ''}>{task.title}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${ptone}`}>{task.priority}</span>
                    {over && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 inline-flex items-center gap-1"><AlertCircle size={10}/> overdue</span>}
                  </div>
                  {task.notes && <div className="text-xs text-slate-600 mt-0.5">{task.notes}</div>}
                  {task.due_at && <div className="text-[11px] text-slate-500 mt-0.5">Due {new Date(task.due_at).toLocaleString()}</div>}
                </div>
                <button onClick={() => remove(task.id)} className="text-slate-300 hover:text-rose-700"><Trash2 size={14}/></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
